/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface BaseControlZoneProps {
  id: string;
  controlZoneId: string;
  baseId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class BaseControlZoneRecord extends Record<BaseControlZoneProps>({
  id: null as any,
  controlZoneId: null as any,
  baseId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    controlZoneId: string;
    baseId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): BaseControlZoneRecord {
    if (json == null) {
      throw new Error('BaseControlZoneRecord.fromJS: parameter cannot be null');
    }
    return new BaseControlZoneRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'BaseControlZoneJson.id',
      ),
      controlZoneId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'controlZoneId', '00000000-0000-0000-0000-000000000000'),
        'BaseControlZoneJson.controlZoneId',
      ),
      baseId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
        'BaseControlZoneJson.baseId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'BaseControlZoneJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    controlZoneId: string;
    baseId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [BaseControlZoneRecord, Error[]] {
    if (json == null) {
      throw new Error('BaseControlZoneRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new BaseControlZoneRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'BaseControlZoneJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controlZoneId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'controlZoneId', '00000000-0000-0000-0000-000000000000'),
            'BaseControlZoneJson.controlZoneId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'baseId', '00000000-0000-0000-0000-000000000000'),
            'BaseControlZoneJson.baseId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'BaseControlZoneJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: BaseControlZoneProps) {
    super(props);
    if (props == null) {
      throw new Error('BaseControlZoneRecord constructor parameter cannot be null');
    }
  }
}

export abstract class BaseControlZoneViewModelBase implements BaseControlZoneProps {
  protected readonly _props: BaseControlZoneProps;
  private _json?: BaseControlZoneProps;

  get id(): string {
    return this._props.id;
  }
  get controlZoneId(): string {
    return this._props.controlZoneId;
  }
  get baseId(): string {
    return this._props.baseId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: BaseControlZoneProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        controlZoneId: this._props.controlZoneId,
        baseId: this._props.baseId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllBaseControlZoneEntities = (state: AppState) =>
  state.entityStores.baseControlZone.entities;

export const selectBaseControlZoneById = (id: string) => (state: AppState) =>
  state.entityStores.baseControlZone.entities.get(id);

export const selectBaseControlZoneMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.baseControlZone.meta.get(id);

export const selectBaseControlZoneAndMetadataById = (id: string) =>
  createSelector(
    selectBaseControlZoneById(id),
    selectBaseControlZoneMetadataById(id),
    (baseControlZone, meta) => ({ baseControlZone, meta }),
  );

export function selectBaseControlZoneEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllBaseControlZoneEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, BaseControlZoneRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectBaseControlZoneEntitiesByControlZoneId(controlZoneId: string) {
  return createSelector(selectAllBaseControlZoneEntities, (entities) =>
    entities.filter((e) => e.controlZoneId === controlZoneId).valueSeq(),
  );
}

export function selectBaseControlZoneEntitiesByBaseId(baseId: string) {
  return createSelector(selectAllBaseControlZoneEntities, (entities) =>
    entities.filter((e) => e.baseId === baseId).valueSeq(),
  );
}

export function selectBaseControlZoneEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllBaseControlZoneEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
